import { ReactNode, useId, useState } from "react";
import Box from "@mui/material/Box";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import { useViewport } from "../../utils/useViewport";
import { visuallyHidden } from "../../utils/visuallyHidden";

const joinClasses = (...names: string[]) => names.filter(Boolean).join(" ");

// The hover tip's look (Tailwind rounded bg-blueGreen p-2 text-xs text-white).
const touchTipSx = {
  bgcolor: "#0F95B3",
  color: "#fff",
  borderRadius: "0.25rem",
  p: 1,
  fontSize: "0.75rem",
  lineHeight: "1rem",
  fontWeight: 400,
};

// At least 8 px from the screen's edges.
const keepOnScreen = [{ name: "preventOverflow", options: { padding: 8 } }];

interface InfoTipProps {
  // The tip text.
  children: ReactNode;
  // Extra wrapper classes, e.g. "ml-1".
  className?: string;
  // Extra classes for the hover tip, e.g. "z-10".
  tipClassName?: string;
  iconFontSize?: string;
}

// An info "i" whose tip shows while the pointer is over it (Tailwind
// group-hover). Touch screens have no hover: there the "i" is a button whose
// tap opens the same tip below it, kept on screen, and a tap on the "i", the
// tip or anywhere else (or Escape) closes it. Screen readers get the tip as
// the button's description, always in the page as on the desktop.
const InfoTip = ({
  children,
  className = "",
  tipClassName = "",
  iconFontSize,
}: InfoTipProps) => {
  const { isTouch } = useViewport();
  const [open, setOpen] = useState(false);
  const tipId = useId();
  const iconSx = { color: "#0F95B3", fontSize: iconFontSize };

  if (isTouch) {
    return (
      <ClickAwayListener onClickAway={() => setOpen(false)}>
        <div className={className}>
          <Tooltip
            open={open}
            onClose={() => setOpen(false)}
            title={children}
            placement="bottom"
            describeChild
            disableFocusListener
            disableHoverListener
            disableTouchListener
            slotProps={{
              popper: { modifiers: keepOnScreen },
              tooltip: { sx: touchTipSx, onClick: () => setOpen(false) },
            }}
          >
            {/* No padding: the size and place of the bare icon it replaces. */}
            <IconButton
              aria-label="More info"
              aria-expanded={open}
              aria-describedby={tipId}
              onClick={() => setOpen((wasOpen) => !wasOpen)}
              sx={{ p: 0 }}
            >
              <InfoIcon sx={iconSx} />
            </IconButton>
          </Tooltip>
          <Box component="span" id={tipId} sx={visuallyHidden}>
            {children}
          </Box>
        </div>
      </ClickAwayListener>
    );
  }

  return (
    <div className={joinClasses("group", className)}>
      <InfoIcon sx={iconSx} className="group-hover:cursor-pointer" />
      <span
        className={joinClasses(
          "absolute",
          tipClassName,
          "ml-2 scale-0 transition-all rounded bg-blueGreen p-2 text-xs text-white group-hover:scale-100",
        )}
      >
        {children}
      </span>
    </div>
  );
};

export default InfoTip;
